import React from 'react';
import { StyleSheet, css, CSSProperties } from 'aphrodite/no-important';
import { useTheme } from '../../../theming/useTheme';
import { style } from '../../../typography/typography.style';
import { createStyleForBoxShadow } from '../../../theming/boxShadow';
import { BaseUnit } from '../../../theming/baseUnitDefinition';

const componentConfigThemeKey = 'button';

interface ButtonProps {
  label: string | null,
  onClick: () => void,
  disabled?: boolean
}

interface ButtonComponentThemeProps {
  backgroundColorEnabled: string
  textColorEnabled: string
  backgroundColorDisabled: string
  textColorDisabled: string
  borderRadius: number
  boxShadowTypeNormal: string
  boxShadowTypeHover: string
  boxShadowTypeActive: string
  boxShadowTypeDisable: string
  baseUnit: BaseUnit

}

type ButtonComponentProps = ButtonProps & ButtonComponentThemeProps;

const getStyle = (backgroundColorEnabled: string, textColorEnabled: string, backgroundColorDisabled: string, textColorDisabled: string, baseUnit: BaseUnit, disabled: boolean, borderRadius: number) => {
  return StyleSheet.create({
    buttonStyle: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: backgroundColorEnabled,
      color: disabled ? textColorDisabled : textColorEnabled,
      cursor: 'pointer',
      outline: 'none',
      border: 'none',
      padding: `${baseUnit / 4}px ${baseUnit / 2}px`,
      borderRadius: `${borderRadius}px`,
      ':disabled': {
        color: textColorDisabled,
        backgroundColor: backgroundColorDisabled,
        cursor: 'auto',
      },
    },
  });
};

export const ButtonComponent: React.FunctionComponent<ButtonComponentProps> = props => {
  const { backgroundColorEnabled, textColorEnabled, backgroundColorDisabled, textColorDisabled, label, onClick, baseUnit, disabled = false, borderRadius, boxShadowTypeActive, boxShadowTypeDisable, boxShadowTypeHover, boxShadowTypeNormal } = props;
  const buttonStyle = getStyle(backgroundColorEnabled, textColorEnabled, backgroundColorDisabled, textColorDisabled, baseUnit, disabled, borderRadius);
  const boxShadowStyle = createStyleForBoxShadow({ boxShadowTypeNormal, boxShadowTypeHover, boxShadowTypeActive, boxShadowTypeDisable });
  return (
    <button
      type="button"
      disabled={disabled}
      className={css(
        buttonStyle.buttonStyle,
        boxShadowStyle.style,
        StyleSheet.create({ style }).style,
      )}
      onClick={onClick}
    >
      {label}
    </button>
  );
};

const mapThemeProps = (theme: ButtonComponentThemeProps) => {
  return {
    backgroundColorEnabled: theme.backgroundColorEnabled,
    textColorEnabled: theme.textColorEnabled,
    backgroundColorDisabled: theme.backgroundColorDisabled,
    textColorDisabled: theme.textColorDisabled,
    borderRadius: theme.borderRadius,
    boxShadowTypeNormal: theme.boxShadowTypeNormal,
    boxShadowTypeHover: theme.boxShadowTypeHover,
    boxShadowTypeActive: theme.boxShadowTypeActive,
    boxShadowTypeDisable: theme.boxShadowTypeDisable,
    baseUnit: theme.baseUnit,

  };
};

export const Button = (props: ButtonProps) => {
  const themeContext = useTheme(mapThemeProps, componentConfigThemeKey);
  return <ButtonComponent {...themeContext} {...props} />;
};
